import { useMemo, useState } from "react";

import ResourceAdmin from "@/components/admin/ResourceAdmin";
import { absoluteUrl, adminEndpoints } from "@/lib/adminApi";

const emptyItem = {
  slug: "", name_ar: "", short_desc_ar: "", long_desc_ar: "",
  specs: [], price_from: 0, sale_price: null, currency: "₪", image_url: "",
  category_id: "", is_featured: false, is_active: true, in_stock: true, order: 0,
};

export default function ProductsAdmin() {
  const [categoryId, setCategoryId] = useState("all");
  const [query, setQuery] = useState("");
  const needle = query.trim().toLowerCase();

  const filterRow = useMemo(
    () => (row) => {
      if (categoryId !== "all" && row.category_id !== categoryId) return false;
      if (!needle) return true;
      const hay = `${row.name_ar || ""} ${row.slug || ""} ${row.short_desc_ar || ""}`.toLowerCase();
      return hay.includes(needle);
    },
    [categoryId, needle],
  );

  const toolbar = ({ items, extra }) => {
    const categories = extra?.categories || [];
    const total = items.length;
    const countByCat = items.reduce((m, p) => {
      m[p.category_id] = (m[p.category_id] || 0) + 1;
      return m;
    }, {});
    return (
      <div className="bg-white border border-slate-100 rounded-2xl p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2" data-testid="products-filter-tabs">
          <button
            type="button"
            onClick={() => setCategoryId("all")}
            className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${
              categoryId === "all"
                ? "bg-[#0A1628] text-white border-[#0A1628]"
                : "bg-white text-slate-700 border-slate-200 hover:border-[#00E5FF]"
            }`}
            data-testid="products-filter-all"
          >
            الكل <span className="opacity-70">({total})</span>
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCategoryId(c.id)}
              className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${
                categoryId === c.id
                  ? "bg-[#0A1628] text-white border-[#0A1628]"
                  : "bg-white text-slate-700 border-slate-200 hover:border-[#00E5FF]"
              }`}
              data-testid={`products-filter-cat-${c.slug}`}
            >
              {c.title_ar} <span className="opacity-70">({countByCat[c.id] || 0})</span>
            </button>
          ))}
        </div>
        <div className="relative">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="ابحث بالاسم، المعرّف، أو الوصف..."
            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-[#0A1628] placeholder:text-slate-400 focus:outline-none focus:border-[#00E5FF] focus:ring-4 focus:ring-cyan-100"
            data-testid="products-search"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-xs"
              data-testid="products-search-clear"
            >
              مسح
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <ResourceAdmin
      title="المنتجات"
      resource="products"
      testIdPrefix="admin-products"
      emptyItem={emptyItem}
      loadExtra={async () => ({
        categories: await adminEndpoints.crud("categories").list(),
      })}
      toolbar={toolbar}
      filterRow={filterRow}
      columns={[
        {
          key: "image_url", label: "الصورة",
          render: (r) => r.image_url ? (
            <img src={absoluteUrl(r.image_url)} alt="" className="w-12 h-12 rounded-lg object-cover" />
          ) : <div className="w-12 h-12 rounded-lg bg-slate-100" />,
        },
        { key: "name_ar", label: "الاسم" },
        { key: "category_title_ar", label: "الفئة" },
        {
          key: "price_from", label: "السعر",
          render: (r) => {
            const hasSale = r.sale_price != null && r.sale_price > 0 && r.sale_price < r.price_from;
            return hasSale ? (
              <div className="flex flex-col leading-tight" dir="ltr">
                <span className="font-bold text-rose-600 text-sm">{r.sale_price} {r.currency}</span>
                <span className="text-slate-400 text-[11px] line-through">{r.price_from} {r.currency}</span>
              </div>
            ) : (
              <span dir="ltr">{r.price_from} {r.currency}</span>
            );
          },
        },
        {
          key: "sale_price", label: "خصم",
          render: (r) => {
            const hasSale = r.sale_price != null && r.sale_price > 0 && r.sale_price < r.price_from;
            if (!hasSale) return <span className="text-slate-400">—</span>;
            const pct = Math.round(((r.price_from - r.sale_price) / r.price_from) * 100);
            return (
              <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-rose-100 text-rose-700">
                -{pct}%
              </span>
            );
          },
        },
        {
          key: "is_featured", label: "مميز",
          render: (r) => r.is_featured ? "✓" : "—",
        },
        {
          key: "is_active", label: "الحالة",
          render: (r) => (
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
              r.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"
            }`}>{r.is_active ? "مفعّل" : "معطّل"}</span>
          ),
        },
        {
          key: "in_stock", label: "المخزون",
          render: (r) => (
            r.in_stock === false
              ? <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-700">نافد</span>
              : <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600">متوفر</span>
          ),
        },
      ]}
      fields={[
        { name: "name_ar", label: "الاسم بالعربية", type: "text" },
        { name: "slug", label: "المعرّف (slug)", type: "text", ltr: true, help: "بالإنجليزية بدون مسافات، يستخدم في الرابط." },
        {
          name: "category_id", label: "الفئة", type: "select",
          options: (extra) => (extra?.categories || []).map((c) => ({ value: c.id, label: c.title_ar })),
        },
        { name: "short_desc_ar", label: "الوصف المختصر", type: "textarea", rows: 2 },
        { name: "long_desc_ar", label: "الوصف الكامل", type: "textarea", rows: 4 },
        {
          name: "specs", label: "المواصفات", type: "kv-list",
          keys: ["label_ar", "value_ar"],
          placeholders: { label_ar: "الوصف (مثل: الدقة)", value_ar: "القيمة (مثل: 5MP)" },
        },
        { name: "price_from", label: "السعر ابتداءً من", type: "number", min: 0 },
        { name: "sale_price", label: "سعر بعد الخصم (اختياري)", type: "number", min: 0, help: "اتركه فارغاً أو 0 لإلغاء الخصم. يجب أن يكون أقل من السعر الأصلي." },
        { name: "currency", label: "العملة", type: "text", ltr: true },
        { name: "image_url", label: "صورة المنتج", type: "image", autoProcess: true },
        { name: "order", label: "ترتيب العرض", type: "number", min: 0 },
        { name: "is_featured", label: "منتج مميز (يظهر في الصفحة الرئيسية)", type: "checkbox" },
        { name: "in_stock", label: "متوفر في المخزون", type: "checkbox" },
        { name: "is_active", label: "مفعّل (يظهر في الموقع العام)", type: "checkbox" },
      ]}
    />
  );
}
